import { differenceInDays } from "date-fns"
import { ExpenseModel, isCompleteExpense } from "../../reduxToolkit/expensesSlice"
import { parseDateInTimeZone } from "../formatDateInTimeZone"

/**
 * Returns the size (in days) of the first 90+ day gap between consecutive
 * complete expenses in an incident, or undefined if there is no such gap.
 *
 * CHM requires expenses separated by 90+ days to be submitted as separate
 * incidents. Shared by the ninety-day-gap todo generator, the todo interstitial
 * page, and `incidentSubmittability`, so the gap size shown in the explainer
 * copy matches the todo and the submit blocker exactly.
 *
 * Lives in lib/rules/ rather than lib/todos/ because it is a plan rule, not todo
 * generation: it outlives the client-side generators (see
 * docs/plans/embeddable-sdk.md). lib/todos/incident.ts re-exports it so existing
 * imports keep resolving.
 */
export function findNinetyDayGapDays(incidentExpenses: ExpenseModel[]): number | undefined {
  // Get all complete expenses with dates, sorted chronologically
  const expensesWithDates = incidentExpenses
    .filter(e => e.date && isCompleteExpense(e) && !e.deleted_at)
    .sort((a, b) => parseDateInTimeZone(a.date!).getTime() - parseDateInTimeZone(b.date!).getTime())

  // Need at least 2 expenses to have a gap
  if (expensesWithDates.length < 2) { return }

  // Check for 90+ day gaps between consecutive expenses
  for (let i = 0; i < expensesWithDates.length - 1; i++) {
    const daysDiff = differenceInDays(
      parseDateInTimeZone(expensesWithDates[i + 1].date!),
      parseDateInTimeZone(expensesWithDates[i].date!)
    )

    if (daysDiff >= 90) {
      return daysDiff
    }
  }
}
